/**
 * A "?" beside a page title that opens what the one-line lead leaves out: the explanation is
 * there for whoever wants it, not in the way of whoever already knows it.
 *
 *     <h1>Servers <plane-help topic="servers">…the explanation…</plane-help></h1>
 *
 * The explanation is the light DOM content, so the page's own styles (links, code) apply to it.
 * It opens below the "?", kept inside the window, and closes on a second click, on Escape and on
 * a click elsewhere.
 */
import { LitElement, html } from 'lit';

import { planeHelpStyles } from '../../styles/components/plane-help.js';

/** Space kept between the open panel and the edge of the window, in pixels. */
const EDGE = 16;

class PlaneHelp extends LitElement {
  static properties = {
    topic: { type: String },
    open: { type: Boolean, reflect: true },
  };

  static styles = planeHelpStyles;

  constructor() {
    super();
    this.topic = '';
    this.open = false;
    this.onOutside = (event) => {
      if (!event.composedPath().includes(this)) {
        this.open = false;
      }
    };
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
    super.disconnectedCallback();
  }

  updated(changed) {
    if (!changed.has('open')) {
      return;
    }
    const panel = this.renderRoot.querySelector('.panel');
    if (this.open) {
      document.addEventListener('click', this.onOutside);
      // back inside the window when the "?" sits too far right for the whole panel
      panel.style.left = '';
      const overflow = panel.getBoundingClientRect().right - (window.innerWidth - EDGE);
      if (overflow > 0) {
        const room = panel.getBoundingClientRect().left - EDGE;
        panel.style.left = `${-Math.min(overflow, room)}px`;
      }
    } else {
      document.removeEventListener('click', this.onOutside);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.open) {
      this.open = false;
      this.renderRoot.querySelector('button').focus();
    }
  }

  render() {
    return html`
      <span class="anchor" @keydown=${(event) => this.onKeydown(event)}>
        <button
          type="button"
          aria-expanded=${this.open ? 'true' : 'false'}
          aria-controls="panel"
          aria-label=${this.topic ? `About ${this.topic}` : 'About this page'}
          title=${this.topic ? `About ${this.topic}` : 'About this page'}
          @click=${() => (this.open = !this.open)}
        >?</button>
        <div class="panel" id="panel" ?hidden=${!this.open}><slot></slot></div>
      </span>
    `;
  }
}

customElements.define('plane-help', PlaneHelp);
